import {
  ScrollArea,
  ScrollAreaViewport,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@data-delivery/d3s';
import React from 'react';

export type ComposableSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  widthClassName?: string;
  footer?:
    | React.ReactNode
    | ((helpers: { close: () => void; open: () => void }) => React.ReactNode);
  children: React.ReactNode;
  className?: string;
  deferClose?: boolean;
  closeDelayMs?: number;
};

export function ComposableSheet({
  open,
  onOpenChange,
  title,
  description,
  side = 'right',
  widthClassName,
  footer,
  children,
  className,
  deferClose = true,
  closeDelayMs = 300,
}: ComposableSheetProps) {
  const [internalOpen, setInternalOpen] = React.useState(open);
  const closeTimerRef = React.useRef<number | undefined>(undefined);

  React.useEffect(() => {
    if (open) {
      setInternalOpen(true);
    } else if (!deferClose) {
      setInternalOpen(false);
    }
  }, [open, deferClose]);

  React.useEffect(
    () => () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = undefined;
      }
    },
    [],
  );

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        setInternalOpen(true);
        onOpenChange(true);
        return;
      }

      if (deferClose) {
        setInternalOpen(false);
        if (closeTimerRef.current) {
          window.clearTimeout(closeTimerRef.current);
        }

        closeTimerRef.current = window.setTimeout(() => {
          onOpenChange(false);
          closeTimerRef.current = undefined;
        }, closeDelayMs);
      } else {
        setInternalOpen(false);
        onOpenChange(false);
      }
    },
    [deferClose, closeDelayMs, onOpenChange],
  );

  const closeHelper = React.useCallback(() => {
    handleOpenChange(false);
  }, [handleOpenChange]);
  const openHelper = React.useCallback(() => {
    handleOpenChange(true);
  }, [handleOpenChange]);
  const helpers = React.useMemo(
    () => ({ close: closeHelper, open: openHelper }),
    [closeHelper, openHelper],
  );

  const footerNode =
    typeof footer === 'function'
      ? (footer as (h: { close: () => void; open: () => void }) => React.ReactNode)(helpers)
      : footer;

  return (
    <Sheet open={internalOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        onPointerDownOutside={e => {
          e.preventDefault();
        }}
        side={side}
        className={widthClassName ?? 'w-full sm:max-w-5xl'}
      >
        <div className='flex h-full flex-col'>
          {title || description ? (
            <SheetHeader>
              {title ? <SheetTitle>{title}</SheetTitle> : null}
              {description ? <SheetDescription>{description}</SheetDescription> : null}
            </SheetHeader>
          ) : null}

          <ScrollArea className={`flex-1 ${className ?? ''}`}>
            <ScrollAreaViewport className='min-h-full'>{children}</ScrollAreaViewport>
          </ScrollArea>

          {footerNode ? (
            <div className='mt-4 border-t pt-4 bg-background'>
              <div className='flex items-center gap-2 justify-start'>{footerNode}</div>
            </div>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default ComposableSheet;
